import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  Avatar,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { reportsService } from '../../services/reports.service';

const ConversationPanel = ({ reportId, userId, reportData }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(400); // Default height
  const [isDragging, setIsDragging] = useState(false);
  const startDragY = useRef(0);
  const startHeight = useRef(0);
  
  // Model selection state
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini'); // Default model
  const [modelMenuAnchor, setModelMenuAnchor] = useState(null);
  
  // Available models
  const availableModels = [
    { id: 'gpt-4', name: 'GPT-4', icon: <AutoFixHighIcon fontSize="small" /> },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini', icon: <AutoFixHighIcon fontSize="small" color="primary" /> },
    { id: 'gpt-4o', name: 'GPT-4o', icon: <AutoFixHighIcon fontSize="small" color="secondary" /> },
  ];

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize or reset conversation when report changes
  useEffect(() => {
    console.log("Report ID changed:", reportId);
    console.log("Report Data:", reportData);
    
    if (reportId && reportData) {
      initializeConversation();
    }
  }, [reportId, reportData]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Add event listeners for resizing
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        const deltaY = e.clientY - startDragY.current;
        const newHeight = Math.max(200, startHeight.current + deltaY); // Minimum height of 200px
        setPanelHeight(newHeight);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    startDragY.current = e.clientY;
    startHeight.current = panelHeight;
    setIsDragging(true);
  };
  
  // Model selection handlers
  const handleModelMenuOpen = (event) => {
    setModelMenuAnchor(event.currentTarget);
  };
  
  const handleModelMenuClose = () => {
    setModelMenuAnchor(null);
  };
  
  const handleModelSelect = (modelId) => {
    setSelectedModel(modelId);
    handleModelMenuClose();
  };

  const initializeConversation = async () => {
    try {
      setIsLoading(true);
      setMessages([]);
      setError(null);
      
      console.log("Initializing conversation for report:", reportId);
      
      if (!reportId || !reportData) {
        console.error('Missing report data or ID');
        setError('Missing report data or ID');
        return;
      }
      
      // Extract insights if they exist
      const insights = reportData.insights || [];
      
      console.log("Creating new conversation with API call to /conversation endpoint");
      
      try {
        // Use the reportsService to create a new conversation
        const response = await reportsService.createOrContinueConversation({
          question: "Initialize conversation with report context",
          report_id: parseInt(reportId, 10),  // Ensure it's a number
          user_id: userId || 'default-user',
          conversation_id: null  // Explicitly set to null for a new conversation
        });
        
        console.log("Full conversation creation response:", response);
        console.log("Conversation created response data:", response.data);
        
        // Check for conversation ID in different possible formats
        let convId = null;
        if (response) {
          if (response.id) {
            convId = response.id;
          } else if (response.conversation_id) {
            convId = response.conversation_id;
          } else if (typeof response === 'string') {
            // Maybe the ID is returned directly as a string
            convId = response;
          }
        }
        
        if (convId) {
          console.log("Conversation ID found:", convId);
          setConversationId(convId);
        } else {
          console.error("No conversation ID found in response:", response.data);
          
          // Create a fallback conversation ID if none is returned
          const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          console.log("Using fallback conversation ID:", fallbackId);
          setConversationId(fallbackId);
          setError('Warning: Using fallback conversation ID');
        }
      } catch (apiError) {
        console.error('API error creating conversation:', apiError);
        console.log('API error response:', apiError.response?.data);
        
        // Create a fallback conversation ID if the API call fails
        const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        console.log("Using fallback conversation ID after API error:", fallbackId);
        setConversationId(fallbackId);
        setError(`API error: ${apiError.message}. Using fallback conversation ID.`);
      }
      
      // Add a system message with the report summary - CHANGED TITLE HERE
      setMessages([
        {
          role: 'system',
          content: `I'm analyzing the "GenAI generated insights" report from ${new Date(reportData.report_date).toLocaleDateString()}.\n\n${reportData.summary}`
        }
      ]);
      
    } catch (error) {
      console.error('Error in conversation initialization:', error);
      setError(`Error initializing conversation: ${error.message}`);
      
      // Create a fallback conversation ID as a last resort
      const fallbackId = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      console.log("Using fallback conversation ID after general error:", fallbackId);
      setConversationId(fallbackId);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    console.log("Send button clicked");
    console.log("Message:", newMessage);
    console.log("Conversation ID:", conversationId);
    console.log("Is Loading:", isLoading);
    console.log("Selected Model:", selectedModel);
    
    if (!newMessage.trim()) {
      console.log("Message is empty, not sending");
      return;
    }
    
    if (isLoading) {
      console.log("Already loading, not sending another message");
      return;
    }
    
    const userMessage = {
      role: 'user',
      content: newMessage
    };
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);
    setError(null);
    
    // Set a timeout warning after 15 seconds
    timeoutRef.current = setTimeout(() => {
      setTimeoutWarning(true);
    }, 15000);
    
    try {
      console.log(`Sending message to conversation ${conversationId}`);
      
      // Check if we're using a fallback ID
      const isFallbackId = conversationId && conversationId.startsWith('fallback-');
      
      if (isFallbackId) {
        // If using fallback ID, simulate a response
        console.log("Using fallback conversation - simulating response");
        
        // Wait a moment to simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const assistantMessage = {
          role: 'assistant',
          content: "I'm sorry, but I'm having trouble connecting to the server. Here's what I can tell you based on the report:\n\n" + 
                   reportData.summary + "\n\n" +
                   "For more specific answers, please try refreshing the page or contact support.",
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Use the reportsService to send a message
        console.log("Sending message to /conversation endpoint");
        
        // Set a timeout for the API call
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
        
        const response = await reportsService.createOrContinueConversation({
          question: userMessage.content,
          report_id: parseInt(reportId, 10),  // Ensure it's a number
          user_id: userId || 'default-user',
          conversation_id: conversationId,
          model_id: selectedModel  // Pass the selected model to the API
        }, {
          signal: controller.signal,
          timeout: 60000 // 60 second timeout
        });
        
        clearTimeout(timeoutId);
        
        console.log("Message response:", response);
        
        if (response && response.response) {
          const assistantMessage = {
            role: 'assistant',
            content: response.response,
            timestamp: new Date().toISOString(),
            model_used: response.model_used || selectedModel
          };
          
          setMessages(prev => [...prev, assistantMessage]);
          
          // Save the conversation ID if it's returned
          if (response.conversation_id && !conversationId) {
            console.log("Setting conversation ID:", response.conversation_id);
            setConversationId(response.conversation_id);
          }
        } else {
          console.error("No response content in API response:", response);
          setError("Received empty response from server");
          
          // Add a fallback response
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: "I'm sorry, but I received an empty response from the server. Please try asking a different question.",
            timestamp: new Date().toISOString(),
            error: true
          }]);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Clear the timeout warning
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setError(`Error: ${error.message}`);
      
      // Add an error message to the conversation
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I'm sorry, but there was an error processing your request: ${error.message}. Please try again later.`,
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setIsLoading(false);
      
      // Clear the timeout warning
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setTimeoutWarning(false);
    }
  };

  // Add a retry function
  const handleRetry = () => {
    // Find the last user message
    const lastUserMessage = [...messages].reverse().find(msg => msg.role === 'user');
    if (lastUserMessage) {
      setNewMessage(lastUserMessage.content);
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        height: `${panelHeight}px`, 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        transition: isDragging ? 'none' : 'height 0.2s ease',
        minHeight: '200px',
        maxHeight: '80vh',
        width: '100%'
      }}
    >
      {/* Header with title and model selector */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, length: '100%' }}>
        <Typography variant="h6">
          Ask about this report
        </Typography>
        
        {/* Subtle model selector */}
        <Tooltip title="Select AI model">
          <Button 
            variant="text" 
            size="small"
            onClick={handleModelMenuOpen}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ 
              textTransform: 'none', 
              color: 'text.secondary',
              fontSize: '0.75rem',
              padding: '2px 8px',
              minWidth: 'auto'
            }}
          >
            {availableModels.find(model => model.id === selectedModel)?.name || 'Select Model'}
          </Button>
        </Tooltip>
        
        <Menu
          anchorEl={modelMenuAnchor}
          open={Boolean(modelMenuAnchor)}
          onClose={handleModelMenuClose}
          PaperProps={{
            elevation: 1,
            sx: { 
              minWidth: 180,
              borderRadius: 1,
              mt: 0.5
            }
          }}
        >
          {availableModels.map((model) => (
            <MenuItem 
              key={model.id} 
              onClick={() => handleModelSelect(model.id)}
              selected={selectedModel === model.id}
              sx={{ 
                fontSize: '0.875rem',
                py: 0.75
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {model.icon}
              </ListItemIcon>
              <ListItemText primary={model.name} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
      
      {/* Scrollable message area - takes up all available space */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          mb: 2, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 1,
          bgcolor: 'background.default',
          p: 1
        }}
      >
        {messages.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            Ask a question about this report to get started
          </Typography>
        ) : (
          messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '80%'
              }}
            >
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                  color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2
                }}
              >
                {message.role === 'assistant' ? (
                  <>
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                    {message.model_used && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
                        {availableModels.find(model => model.id === message.model_used)?.name || message.model_used}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography>{message.content}</Typography>
                )}
              </Paper>
            </Box>
          ))
        )}
        
        {error && (
          <Box sx={{ mb: 2, alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
              <Typography>{error}</Typography>
            </Paper>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Retry
            </Button>
          </Box>
        )}
        
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>
      
      {/* Input area - always at the bottom */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Ask a question about this report..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isLoading}
            multiline
            maxRows={3}
            sx={{ mr: 1 }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            endIcon={<SendIcon />} 
            onClick={handleSendMessage}
            disabled={isLoading || !newMessage.trim()}
          >
            Send
          </Button>
        </Box>
      </Box>
      
      {/* Resize handle */}
      <Box 
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '10px',
          cursor: 'ns-resize',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)'
          }
        }}
        onMouseDown={handleResizeStart}
      >
        <DragHandleIcon fontSize="small" color="action" />
      </Box>
      
      <Snackbar 
        open={timeoutWarning} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info">
          This is taking longer than expected. The AI is processing your complex question...
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ConversationPanel; 